/**
 * importBrandRouting — which brand a bulk-imported Shopify row belongs under.
 *
 * One shop can sell several dial brands. TimeFactors' catalogue is 41 watches
 * under seven vendors: Smiths, Precista, Sewills and Armstrong Siddeley are
 * brands TimeFactors acquired and each has its own brand doc; Dreadnought and
 * Speedbird are TimeFactors' own house lines. Bulk import wrote all of them
 * under the importing brand, so a Smiths Everest landed as a TimeFactors watch.
 *
 * Three decisions live here, all pure:
 *
 *   1. DETECTION — which brand the shop's own data says the row is
 *      ({@link detectDialBrand}).
 *   2. PLACEMENT — where the row's document goes, which is not always what
 *      detection says: a reference that already exists under the importing brand
 *      or one of its acquired siblings stays exactly where it is
 *      ({@link resolveProductPlacement}).
 *   3. STAMP — what to record on the reference about how its brand was chosen
 *      ({@link decideBrandStamp}).
 *
 * Routing is only ever ACTIVE for a brand with at least one acquisition that
 * links to an existing brand doc ({@link buildRoutingContext}). For every other
 * brand the importer behaves as it always has, and stamps nothing.
 *
 * Free of Firebase, so all of it is tested against the real TimeFactors
 * catalogue (`__fixtures__/timefactorsProducts.json`).
 */

/** The little of a brand doc routing needs. */
export interface RoutingBrand {
  id: string;
  name: string;
  displayName?: string;
  alternativeNames?: string[];
}

export interface BrandRoutingContext {
  /** The brand the import was started from. Always the first option. */
  importing: RoutingBrand;
  /** Acquired brands that have a brand doc, in `acquisitions[]` order. */
  siblings: RoutingBrand[];
}

/**
 * The acquired-brand ids worth looking up, in order, deduped. A `name_change`
 * entry records a former name rather than a separate brand and carries no id, so
 * it drops out here without needing a filter on the type.
 */
export function acquisitionSiblingIds(brand: {
  id: string;
  acquisitions?: readonly { acquiredBrandId?: string }[];
}): string[] {
  const ids: string[] = [];
  for (const entry of brand.acquisitions ?? []) {
    const id = entry?.acquiredBrandId?.trim();
    if (id && id !== brand.id && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

/**
 * Routing context for an import, or null when it does not apply — no
 * acquisition resolves to an existing brand doc. `siblingDocs` is whatever the
 * caller managed to load; a missing doc (Richmond Spencer has none) is simply
 * not an option.
 */
export function buildRoutingContext(
  importing: RoutingBrand & { acquisitions?: readonly { acquiredBrandId?: string }[] },
  siblingDocs: ReadonlyMap<string, RoutingBrand | null | undefined>,
): BrandRoutingContext | null {
  const siblings: RoutingBrand[] = [];
  for (const id of acquisitionSiblingIds(importing)) {
    const doc = siblingDocs.get(id);
    if (doc) siblings.push(doc);
  }
  if (siblings.length === 0) return null;
  return { importing, siblings };
}

/** Every brand a row may be placed under: the importing brand, then its siblings. */
export function routingOptions(ctx: BrandRoutingContext): RoutingBrand[] {
  return [ctx.importing, ...ctx.siblings];
}

// ── Detection ────────────────────────────────────────────────────────────────

/** Lower-cased words of a label, accents folded, punctuation dropped. */
function words(value: string | null | undefined): string[] {
  return (value ?? '')
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
}

/** Spacing-insensitive identity: "Time Factors" and "Timefactors" are one name. */
function squash(value: string | null | undefined): string {
  return words(value).join('');
}

function brandNames(brand: RoutingBrand): string[] {
  return [brand.name, brand.displayName, ...(brand.alternativeNames ?? [])].filter(
    (n): n is string => typeof n === 'string' && n.trim().length > 0,
  );
}

/** The option whose name a single label (a vendor, a tag) IS, or undefined. */
function brandNamedBy(label: string | null | undefined, options: RoutingBrand[]): RoutingBrand | undefined {
  const key = squash(label);
  if (!key) return undefined;
  return options.find((brand) => brandNames(brand).some((name) => squash(name) === key));
}

/**
 * Whether free text mentions a brand, on word boundaries: the brand's words in
 * order, or run together as one word ("Timefactors" for "Time Factors").
 */
function textMentions(text: string | null | undefined, brand: RoutingBrand): boolean {
  const haystack = words(text);
  if (haystack.length === 0) return false;
  return brandNames(brand).some((name) => {
    const needle = words(name);
    if (needle.length === 0) return false;
    const joined = needle.join('');
    for (let i = 0; i < haystack.length; i += 1) {
      if (haystack[i] === joined) return true;
      if (needle.every((w, j) => haystack[i + j] === w)) return true;
      // The reverse spelling: a one-word brand written as two words in the text.
      if (needle.length === 1 && i + 1 < haystack.length && haystack[i] + haystack[i + 1] === joined) {
        return true;
      }
    }
    return false;
  });
}

function splitTags(tags: string | readonly string[] | null | undefined): string[] {
  if (!tags) return [];
  const list = typeof tags === 'string' ? tags.split(',') : [...tags];
  return list.map((t) => String(t ?? '').trim()).filter(Boolean);
}

export interface BrandSignals {
  vendor?: string | null;
  /** Shopify tags — the array, or the comma-joined string the scraper keeps. */
  tags?: string | readonly string[] | null;
  title?: string | null;
  handle?: string | null;
}

export type BrandDetectionSource = 'vendor' | 'tag' | 'title' | 'fallback';

export interface BrandDetection {
  brandId: string;
  source: BrandDetectionSource;
  /** Nothing named a known brand, so the importing brand was assumed. */
  fallback: boolean;
  /**
   * The structured answer and the title do not agree: the title names a
   * different brand, or does not name the detected sibling at all. TimeFactors'
   * `SPEEDBIRD GMT PRS-22GMTQ` is the case — vendor Smiths, nothing in the title
   * says Smiths — and it is the one row of 41 detection gets wrong.
   */
  vendorTitleDisagree: boolean;
}

/**
 * The brand a row's own data says it is.
 *
 *   1. vendor — the base signal (41 of 41 filled on TimeFactors, 40 right);
 *   2. a tag that names a DIFFERENT sibling overrides it: the tag is structured
 *      too, and a shop that bothers to tag a row "Precista" means it. A tag
 *      naming the importing brand never overrides — a house tag on every product
 *      is the shop, not the dial;
 *   3. with no vendor match, a tag naming any option;
 *   4. with neither, the title or handle, when exactly one brand is mentioned;
 *   5. otherwise the importing brand, flagged as a fallback.
 */
export function detectDialBrand(signals: BrandSignals, ctx: BrandRoutingContext): BrandDetection {
  const options = routingOptions(ctx);
  const byVendor = brandNamedBy(signals.vendor, options);

  const tagged = new Map<string, RoutingBrand>();
  for (const tag of splitTags(signals.tags)) {
    const brand = brandNamedBy(tag, options);
    if (brand) tagged.set(brand.id, brand);
  }
  const siblingTags = [...tagged.values()].filter((b) => b.id !== ctx.importing.id);
  // Two different siblings tagged is no answer at all.
  const tagSibling = siblingTags.length === 1 ? siblingTags[0] : undefined;
  const tagAny = tagged.size === 1 ? [...tagged.values()][0] : undefined;

  const inTitle = options.filter((b) => textMentions(signals.title, b));
  const inText = options.filter(
    (b) => inTitle.includes(b) || textMentions(signals.handle, b),
  );

  let chosen: RoutingBrand | undefined;
  let source: BrandDetectionSource;
  if (byVendor && tagSibling && tagSibling.id !== byVendor.id) {
    chosen = tagSibling;
    source = 'tag';
  } else if (byVendor) {
    chosen = byVendor;
    source = 'vendor';
  } else if (tagSibling ?? tagAny) {
    chosen = tagSibling ?? tagAny;
    source = 'tag';
  } else if (inText.length === 1) {
    chosen = inText[0];
    source = 'title';
  } else {
    source = 'fallback';
  }

  if (!chosen) {
    return {
      brandId: ctx.importing.id,
      source: 'fallback',
      fallback: true,
      vendorTitleDisagree: false,
    };
  }

  const structured = source === 'vendor' || source === 'tag';
  const titleNamesAnother = inTitle.some((b) => b.id !== chosen.id);
  const siblingMissingFromTitle =
    chosen.id !== ctx.importing.id && !!signals.title?.trim() && !inTitle.includes(chosen);
  return {
    brandId: chosen.id,
    source,
    fallback: false,
    vendorTitleDisagree: structured && (titleNamesAnother || siblingMissingFromTitle),
  };
}

// ── Placement ────────────────────────────────────────────────────────────────

/** Where one document of a product goes, and whether it is already there. */
export interface DocPlacement {
  brandId: string;
  existing: boolean;
}

export interface ProductPlacement {
  /** Per requested doc id. */
  docs: Map<string, DocPlacement>;
  /** Distinct brands that already hold a document of this product. */
  existingBrandIds: string[];
  /**
   * The product's documents already sit under MORE than one brand (or one doc
   * exists twice). Each is updated where it is; nothing is stamped, because no
   * single answer to "which brand is this product" can be read off that.
   */
  split: boolean;
}

/**
 * Sibling-wide duplicate check for one Shopify product.
 *
 * Document ids are `shopify__{productId}__{variantId}` (and `…__parent`), which
 * do not mention the brand, and the Move References tool keeps them — so a
 * reference a curator already moved to Smiths is found there, and stays there.
 *
 *   - a document that exists is placed where it is (first hit in `brandOrder`,
 *     which puts the importing brand first, when it exists more than once);
 *   - a document that does not exist follows the family: the brand holding the
 *     parent, else the only brand holding any of the product's documents, else
 *     the detected brand.
 */
export function resolveProductPlacement(
  docIds: readonly string[],
  parentDocId: string | undefined,
  existsUnder: (brandId: string, docId: string) => boolean,
  brandOrder: readonly string[],
  detectedBrandId: string,
): ProductPlacement {
  const docs = new Map<string, DocPlacement>();
  const holders = new Set<string>();
  let duplicated = false;

  const all = parentDocId && !docIds.includes(parentDocId) ? [...docIds, parentDocId] : [...docIds];
  for (const docId of all) {
    const found = brandOrder.filter((brandId) => existsUnder(brandId, docId));
    if (found.length === 0) continue;
    if (found.length > 1) duplicated = true;
    docs.set(docId, { brandId: found[0], existing: true });
    for (const brandId of found) holders.add(brandId);
  }

  const existingBrandIds = brandOrder.filter((id) => holders.has(id));
  const parentHolder = parentDocId ? docs.get(parentDocId)?.brandId : undefined;
  const familyBrand =
    parentHolder ?? (existingBrandIds.length === 1 ? existingBrandIds[0] : undefined) ?? detectedBrandId;
  for (const docId of all) {
    if (!docs.has(docId)) docs.set(docId, { brandId: familyBrand, existing: false });
  }

  return { docs, existingBrandIds, split: duplicated || existingBrandIds.length > 1 };
}

// ── Stamp ────────────────────────────────────────────────────────────────────

export type BrandAssignedBy = 'detection' | 'user';

export interface BrandStampInput {
  /** What the reference already records, if anything. */
  existingStamp?: BrandAssignedBy | null;
  /** The brand the document is (or is about to be) under. */
  placedBrandId: string;
  /** What detection alone would have chosen. */
  detectedBrandId: string;
  /** The curator picked the brand for this row by hand. */
  userOverride?: boolean;
  /** The product is split across brands — see {@link ProductPlacement.split}. */
  split?: boolean;
}

/**
 * The value to write to `brandAssignedBy`, or null to leave the reference's
 * stamp exactly as it is (in which case `brandAssignedAt` is not touched
 * either — the timestamp moves only when the stamp is set or changed).
 *
 *   - placed where detection says        → 'detection'
 *   - placed somewhere detection does not → 'user': someone put it there, whether
 *     with this import's dropdown or by moving the reference earlier
 *   - an existing 'user' is never downgraded
 *   - a stamp that already says the right thing is not rewritten
 *   - a split product is not stamped at all
 */
export function decideBrandStamp(input: BrandStampInput): BrandAssignedBy | null {
  if (input.split) return null;
  if (input.existingStamp === 'user') return null;
  const wanted: BrandAssignedBy =
    input.userOverride || input.placedBrandId !== input.detectedBrandId ? 'user' : 'detection';
  return input.existingStamp === wanted ? null : wanted;
}

// ── The whole run ────────────────────────────────────────────────────────────

/** One Shopify product in the run: its documents and what the shop says about it. */
export interface RoutingProduct {
  productId: string;
  /** For the report only. */
  label: string;
  variantDocIds: string[];
  parentDocId: string;
  signals: BrandSignals;
}

export interface RoutedDoc {
  brandId: string;
  existing: boolean;
  /** Value for `brandAssignedBy`, or null to leave the stamp alone. */
  stamp: BrandAssignedBy | null;
}

/** What the preview needs to know about one product. */
export interface RoutedProduct {
  detection: BrandDetection;
  /**
   * Brands already holding a document of this product. Non-empty means the
   * product is LOCKED: it was imported before, and an import never moves a
   * reference — the brand editor's Move tool does.
   */
  existingBrandIds: string[];
  /** Where new documents of this product go: the family's brand, the curator's pick, or detection. */
  brandId: string;
  /** The curator picked a brand other than the one detection chose. */
  userOverride: boolean;
  split: boolean;
}

export interface RoutingPlan {
  /** Every variant and parent document of the run, by doc id. */
  docs: Map<string, RoutedDoc>;
  detections: Map<string, BrandDetection>;
  products: Map<string, RoutedProduct>;
  /** Variant/standalone documents per brand — parents are not counted. */
  perBrand: Map<string, number>;
  /**
   * Products that want a look: a NEW product detection had to fall back on, or
   * any product whose vendor and title disagree.
   */
  flagged: number;
  split: { productId: string; label: string; brandIds: string[] }[];
}

/**
 * Detection, placement and stamp for every product of a run. `lookup` answers
 * "what is stored at this doc id under this brand" — the stamp it carries, or
 * undefined when there is no such document.
 *
 * `chosen` is the curator's per-product pick from the preview. It only ever
 * applies to a product with no existing document: one that was imported before
 * stays where it is, whatever was picked. A pick that is not one of the options
 * is ignored.
 */
export function planImportRouting(
  products: readonly RoutingProduct[],
  ctx: BrandRoutingContext,
  lookup: (brandId: string, docId: string) => { brandAssignedBy?: BrandAssignedBy | null } | undefined,
  chosen: ReadonlyMap<string, string> = new Map(),
): RoutingPlan {
  const brandOrder = routingOptions(ctx).map((b) => b.id);
  const plan: RoutingPlan = {
    docs: new Map(),
    detections: new Map(),
    products: new Map(),
    perBrand: new Map(),
    flagged: 0,
    split: [],
  };
  for (const product of products) {
    const detection = detectDialBrand(product.signals, ctx);
    plan.detections.set(product.productId, detection);

    const picked = chosen.get(product.productId);
    const pick = picked && brandOrder.includes(picked) ? picked : detection.brandId;
    const placement = resolveProductPlacement(
      product.variantDocIds,
      product.parentDocId,
      (brandId, docId) => lookup(brandId, docId) !== undefined,
      brandOrder,
      pick,
    );
    const locked = placement.existingBrandIds.length > 0;
    // Same rule as the row flags (rowBrandView): a fallback is news only for a
    // product nobody has imported yet.
    if ((detection.fallback && !locked) || detection.vendorTitleDisagree) plan.flagged += 1;
    const newDocBrand =
      [...placement.docs.values()].find((d) => !d.existing)?.brandId ??
      placement.docs.get(product.parentDocId)?.brandId ??
      placement.existingBrandIds[0] ??
      pick;
    plan.products.set(product.productId, {
      detection,
      existingBrandIds: placement.existingBrandIds,
      brandId: locked ? newDocBrand : pick,
      userOverride: !locked && pick !== detection.brandId,
      split: placement.split,
    });
    if (placement.split) {
      plan.split.push({
        productId: product.productId,
        label: product.label,
        brandIds: placement.existingBrandIds,
      });
    }
    for (const [docId, placed] of placement.docs) {
      plan.docs.set(docId, {
        brandId: placed.brandId,
        existing: placed.existing,
        stamp: decideBrandStamp({
          existingStamp: placed.existing ? lookup(placed.brandId, docId)?.brandAssignedBy : undefined,
          placedBrandId: placed.brandId,
          detectedBrandId: detection.brandId,
          split: placement.split,
        }),
      });
      if (docId !== product.parentDocId) {
        plan.perBrand.set(placed.brandId, (plan.perBrand.get(placed.brandId) ?? 0) + 1);
      }
    }
  }
  return plan;
}

// ── Preview ──────────────────────────────────────────────────────────────────

/** Dropdown label: the importing brand by name, a sibling with how it relates. */
export function routingOptionLabel(brand: RoutingBrand, ctx: BrandRoutingContext): string {
  const name = brand.displayName || brand.name;
  if (brand.id === ctx.importing.id) return name;
  return `${name} — acquired by ${ctx.importing.displayName || ctx.importing.name}`;
}

export type RowBrandFlag = 'unknown-vendor' | 'vendor-title-disagree' | 'existing-elsewhere' | 'split';

export interface RowBrandView {
  /** The brand this row's document is, or will be, under. */
  brandId: string;
  /** Set when the row's document already exists: the brand it cannot be moved from. */
  lockedUnderBrandId?: string;
  /** Why this row wants a second look. Empty for the ordinary case. */
  flags: RowBrandFlag[];
}

/**
 * How one preview row presents its brand. A row is locked when ITS document
 * exists, or — for a new variant of a product imported before — when the family
 * does: either way the import will not put it anywhere else.
 */
export function rowBrandView(plan: RoutingPlan, productId: string, docId: string): RowBrandView | undefined {
  const product = plan.products.get(productId);
  const doc = plan.docs.get(docId);
  if (!product || !doc) return undefined;
  const locked = product.existingBrandIds.length > 0;
  const flags: RowBrandFlag[] = [];
  if (product.split) flags.push('split');
  // A fallback is a nudge for a product nobody has placed yet. Once one of its
  // references exists anywhere in the sibling set the brand is settled, and
  // flagging it again on every re-import is noise, not news.
  if (product.detection.fallback && !locked) flags.push('unknown-vendor');
  if (product.detection.vendorTitleDisagree) flags.push('vendor-title-disagree');
  if (locked && doc.brandId !== product.detection.brandId) flags.push('existing-elsewhere');
  return {
    brandId: doc.brandId,
    ...(locked ? { lockedUnderBrandId: doc.brandId } : {}),
    flags,
  };
}
